import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@nxsys/store';
import { socketConversation } from '@nxsys/services/sockets';
import { employeeMessageActions } from '@nxsys/store/slices/employeeMessage/reducer/employeeMessageSlice';
import { fetchUnreadCount } from '@nxsys/store/slices/message/actions';
import { ChatMessage, Conversation } from '@nxsys/store/slices/message/types';

type UseEmployeeMessageSocketOptions = {
  userId?: string;
  agencyId?: string;
  companyParentId?: string;
  isAdmin?: boolean;
};

export const useEmployeeMessageSocket = ({
  userId,
  agencyId,
  companyParentId,
  isAdmin,
}: UseEmployeeMessageSocketOptions): void => {
  const dispatch = useDispatch<AppDispatch>();
  const processedMessageIds = useRef<Set<number>>(new Set());

  const handleNewMessage = useCallback(
    (eventData: {
      data: ChatMessage;
      unreadCount?: number;
      success: boolean;
    }) => {
      if (!eventData?.success || !eventData?.data) return;
      const message = eventData.data;
      if (processedMessageIds.current.has(message.id)) return;
      processedMessageIds.current.add(message.id);
      // Clean up old IDs
      if (processedMessageIds.current.size > 500) {
        const ids = Array.from(processedMessageIds.current);
        processedMessageIds.current = new Set(ids.slice(-250));
      }
      dispatch(
        employeeMessageActions.onNewMessage({
          message,
          unreadCount: eventData.unreadCount,
          currentUserId: userId,
        }),
      );
      // Update global unread count
      dispatch(fetchUnreadCount());
    },
    [dispatch, userId],
  );

  const handleConversationRead = useCallback(
    (eventData: { data: Conversation; success: boolean }) => {
      if (!eventData?.success || !eventData?.data) return;
      dispatch(employeeMessageActions.onConversationRead(eventData.data));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!userId) return;

    const events: [string, (...args: any[]) => void][] = [];

    // User-level events
    events.push([`new-message-${userId}`, handleNewMessage]);
    events.push([`conversation-read-${userId}`, handleConversationRead]);

    // Agency-level events
    if (agencyId) {
      events.push([`new-message-agency-${agencyId}`, handleNewMessage]);
      events.push([
        `conversation-read-agency-${agencyId}`,
        handleConversationRead,
      ]);
    }

    // Broadcast events (admin only)
    if (isAdmin && companyParentId) {
      events.push([
        `new-message-broadcast-${companyParentId}`,
        handleNewMessage,
      ]);
      events.push([
        `conversation-read-broadcast-${companyParentId}`,
        handleConversationRead,
      ]);
    }

    // Register all listeners
    for (const [event, handler] of events) {
      socketConversation.on(event, handler);
    }

    return () => {
      for (const [event, handler] of events) {
        socketConversation.off(event, handler);
      }
    };
  }, [
    userId,
    agencyId,
    companyParentId,
    isAdmin,
    handleNewMessage,
    handleConversationRead,
  ]);
};

export default useEmployeeMessageSocket;
